/**
 * One module's draft, its save and its edit window: the module half of the
 * edit session.
 *
 * The controller keeps one per module it has opened, so a draft whose save
 * could not land outlives the editor that typed it, and the session's
 * `pending` and `leave` answer for it.
 *
 * @module platform/module-edit-target
 */

import { produce } from "immer";

import {
  asError,
  carryEdits,
  clone,
  rebaseCarried,
  serialized,
} from "@/components/studio/module-editor-model";
import {
  type ModuleSaveConflict,
  readModuleConflict,
} from "@/components/studio/module-save-conflict";
import type {
  ModuleWorkspaceRecord,
  ProgrammePlatformClient,
  SaveModuleWorkspaceInput,
} from "@/platform/client";
import type { Module } from "@/types";
import { stableJson } from "@/utils/stable-json";

/** The edit window, as the programme's: a typing pause long enough to be one change. */
const MODULE_EDIT_WINDOW_MS = 400;

export type ModuleSaveStatus = "idle" | "saving" | "saved" | "error";

/** What a module edit changes: the module and the programme's PLO mappings beside it. */
export interface ModuleDraft {
  module: Module;
  ploToMimlos: Record<string, string[]>;
}

export interface ModuleTargetState {
  /** The record the draft is based on: what the next save composes against. */
  record: ModuleWorkspaceRecord | null;
  module: Module | null;
  ploToMimlos: Record<string, string[]>;
  /** The draft as last read or saved, serialized. */
  baseline: string;
  status: ModuleSaveStatus;
  error: Error | null;
  conflict: ModuleSaveConflict | null;
}

/** What only the open editor knows: the lease, whether it may write, what follows a save. */
export interface ModuleSaveAdapter {
  client: Pick<ProgrammePlatformClient, "saveModuleWorkspace">;
  changeCaseId?: string;
  editLeaseId: () => string | undefined;
  canEdit: () => boolean;
  /** A save's answer is now what is on screen, before anything renders it. */
  onAdopted?: () => void;
  onSaved?: () => Promise<void> | void;
}

type Update<T> = T | ((current: T) => T);

const EMPTY: ModuleTargetState = {
  record: null,
  module: null,
  ploToMimlos: {},
  baseline: "",
  status: "idle",
  error: null,
  conflict: null,
};

export class ModuleEditTarget {
  private state = EMPTY;

  private readonly listeners = new Set<() => void>();

  private timer: ReturnType<typeof setTimeout> | null = null;

  private adapter: ModuleSaveAdapter | null = null;

  private inFlight: { sending: Promise<boolean>; draft: string | null; done: boolean } | null =
    null;

  /** Bumped by a discard, so a save landing after it adopts nothing and calls back no one. */
  private discards = 0;

  /** What a draft seeded before any read was typed over, until a read replaces it. */
  private seeded: ModuleDraft | null = null;

  /** A read asked for through `load`, until it answers either way. */
  private loading: Promise<unknown> | null = null;

  /** The baseline's module, serialized once per baseline. */
  private baselineModule: { from: string; key: string } | null = null;

  constructor(private readonly windowMs = MODULE_EDIT_WINDOW_MS) {}

  readonly subscribe = (listener: () => void): (() => void) => {
    this.listeners.add(listener);
    return () => {
      this.listeners.delete(listener);
    };
  };

  readonly getState = (): ModuleTargetState => this.state;

  /**
   * The module itself differs from what was last read or saved, rather than
   * only its mappings: a draft Module Studio typed, which only it can resolve.
   */
  get moduleEdited(): boolean {
    const { module, baseline } = this.state;
    if (!module || !baseline) {
      return false;
    }
    if (this.baselineModule?.from !== baseline) {
      const parsed = JSON.parse(baseline) as { module: Module };
      this.baselineModule = { from: baseline, key: stableJson(parsed.module) };
    }
    return stableJson(module) !== this.baselineModule.key;
  }

  /** Something is attached to send the draft. */
  get attached(): boolean {
    return this.adapter !== null;
  }

  /** The draft differs from what was last read or saved. */
  get pending(): boolean {
    const draft = this.draftKey();
    return draft !== null && draft !== this.state.baseline;
  }

  /**
   * Saves through `adapter` while the editor is open. A draft left with
   * nothing attached stays pending, for the session's departure to answer.
   * @returns The detach.
   */
  attach(adapter: ModuleSaveAdapter): () => void {
    if (this.adapter !== adapter) {
      this.adapter = adapter;
      // A fresh state, so a reader of `attached` hears of it; `set` resumes.
      this.set({});
    }
    this.resume();
    return () => {
      if (this.adapter === adapter) {
        this.adapter = null;
        this.disarm();
        this.set({});
      }
    };
  }

  edit(
    recipe: (draft: ModuleDraft) => void,
    { save = "debounced" }: { save?: "debounced" | "now" } = {},
  ): void {
    const { module, ploToMimlos, status } = this.state;
    if (!module) {
      return;
    }
    const next = produce({ module, ploToMimlos }, recipe);
    // A save out stays one, or the next keystroke would start another over it
    // against the same revision. A failed one stays too, as its notice is the
    // reader's only way to answer it, until an edit typed the draft back.
    const typedBack = serialized(next.module, next.ploToMimlos) === this.state.baseline;
    const held = status === "saving" || (status === "error" && !typedBack);
    // A finished save answered for a draft typed away, not one typed again alike.
    if (typedBack && this.inFlight?.done) {
      this.inFlight = null;
    }
    this.set({
      module: next.module,
      ploToMimlos: next.ploToMimlos,
      status: held ? status : "idle",
      ...(typedBack && status !== "saving" ? { error: null, conflict: null } : {}),
    });
    if (save === "now") {
      this.flush().catch(() => undefined);
      return;
    }
    this.arm();
  }

  /**
   * Start a draft from what the reader sees, so an edit can land on it before
   * the module's record is read. It is held unsent until `reopen` carries it
   * over that record. A target already holding a draft is left alone.
   */
  seed(draft: ModuleDraft): void {
    if (this.state.module) {
      return;
    }
    this.seeded = clone(draft);
    this.set({
      module: draft.module,
      ploToMimlos: draft.ploToMimlos,
      baseline: serialized(draft.module, draft.ploToMimlos),
    });
  }

  /**
   * Read the record a seeded draft waits on. `leave` waits for it, and the
   * answer lands through `land` only on that draft: not after a discard, and
   * not over a record another read brought meanwhile.
   * @returns Whether it landed; rejects when the read failed.
   */
  load(
    read: Promise<ModuleWorkspaceRecord>,
    land: (record: ModuleWorkspaceRecord) => void = (record) => {
      this.reopen(record);
    },
  ): Promise<boolean> {
    const discards = this.discards;
    const landed = read.then((record) => {
      // A record taken since, as by Module Studio's own read, is newer.
      if (discards !== this.discards || this.state.record) {
        return false;
      }
      land(record);
      return true;
    });
    const loading = landed.catch(() => false);
    this.loading = loading;
    loading
      .finally(() => {
        if (this.loading === loading) {
          this.loading = null;
        }
      })
      .catch(() => undefined);
    return landed;
  }

  /** Arm the window again for a draft something stopped holding, such as an editor now able to write. */
  resume(): void {
    if (!this.timer && this.sendable()) {
      this.arm();
    }
  }

  /**
   * Adopt a freshly read record, the draft and its baseline together. Given
   * the save that returned it, what was typed while it was out is carried over.
   * @returns The record's module.
   */
  adopt(next: ModuleWorkspaceRecord, sent?: SaveModuleWorkspaceInput): Module {
    const nextModule = clone(next.module);
    const nextMappings = clone(next.ploToMimlos);
    const { module, ploToMimlos } = this.state;
    this.seeded = null;
    this.set({
      record: sent ? rebaseCarried(next, sent, { module, ploToMimlos }) : next,
      module: carryEdits(sent?.module, module, nextModule),
      ploToMimlos: carryEdits(sent?.ploToMimlos, ploToMimlos, nextMappings),
      baseline: serialized(nextModule, nextMappings),
    });
    return nextModule;
  }

  /**
   * Adopt a record read on reopening. A draft left unsent is carried over it,
   * each property it changed still based on the value it was typed over, so
   * the save surfaces a peer's change there as a conflict.
   */
  reopen(next: ModuleWorkspaceRecord): Module {
    const { record, module, ploToMimlos } = this.state;
    const typedOver = record
      ? { module: record.module, ploToMimlos: record.ploToMimlos }
      : this.seeded;
    if (!typedOver || !module || !this.pending) {
      // An old save's failure has nothing left to answer for.
      this.set({ status: "idle", error: null, conflict: null });
      return this.adopt(next);
    }
    this.seeded = null;
    const nextModule = clone(next.module);
    const nextMappings = clone(next.ploToMimlos);
    this.set({
      record: {
        ...next,
        module: carryEdits(typedOver.module, module, next.module, typedOver.module),
        ploToMimlos: carryEdits(
          typedOver.ploToMimlos,
          ploToMimlos,
          next.ploToMimlos,
          typedOver.ploToMimlos,
        ),
      },
      module: carryEdits(typedOver.module, module, nextModule),
      ploToMimlos: carryEdits(typedOver.ploToMimlos, ploToMimlos, nextMappings),
      // Status stays: a save still out is still the one, and a failure still
      // waits on the reader's answer rather than resending on its own.
      baseline: serialized(nextModule, nextMappings),
    });
    return nextModule;
  }

  /** Whether the draft is on the platform once this resolves. */
  flush(): Promise<boolean> {
    const { record, module, ploToMimlos } = this.state;
    const adapter = this.adapter;
    if (!record || !module || !adapter?.canEdit() || !this.pending) {
      // Nothing pending is a landed save to a caller waiting on one. Unable to
      // write is not, and a caller that flushes before acting needs the two
      // told apart.
      return Promise.resolve(!this.pending);
    }
    this.disarm();
    this.set({ status: "saving", error: null });
    const input: SaveModuleWorkspaceInput = {
      programmeId: record.programmeId,
      moduleId: module.id,
      module,
      ploToMimlos,
      expectedRevision: record.programmeRevision,
      expectedModuleHash: record.moduleContentHash,
      baseModule: record.module,
      basePloToMimlos: record.ploToMimlos,
      editLeaseId: adapter.editLeaseId(),
      changeCaseId: adapter.changeCaseId,
    };
    const entry = { sending: this.send(adapter, input), draft: this.draftKey(), done: false };
    this.inFlight = entry;
    entry.sending
      .finally(() => {
        entry.done = true;
      })
      .catch(() => undefined);
    return entry.sending;
  }

  /** Send what is pending and wait. False when it cannot land. */
  leave(): Promise<boolean> {
    const { loading } = this;
    if (loading && !this.state.record) {
      // A seeded draft has nothing to send until its record is read.
      return loading.then(() => (this.state.record ? this.leave() : !this.pending));
    }
    const sent = this.inFlight;
    if (!this.pending) {
      // A save still out carries the editor's lease, which must outlive it.
      // Whatever it answers, nothing is left to lose.
      return sent && !sent.done
        ? sent.sending.then(
            () => true,
            () => true,
          )
        : Promise.resolve(true);
    }
    // The draft on screen is the one already sent: its answer is the answer.
    if (sent?.draft === this.draftKey()) {
      return sent.sending.catch(() => false);
    }
    // Typed while a save is out, sending now would carry its stale revision.
    const { status, conflict } = this.state;
    if (status === "error" || conflict || !this.adapter?.canEdit() || (sent && !sent.done)) {
      return Promise.resolve(false);
    }
    return this.flush();
  }

  /** Drop the draft unsent, as the reader chose on leaving. */
  discard(): void {
    this.discards += 1;
    this.seeded = null;
    this.disarm();
    // A save still out is waited on by `leave`; a finished one answered for a
    // draft that is gone, and must not answer for the next one typed alike.
    if (this.inFlight?.done) {
      this.inFlight = null;
    }
    this.state = EMPTY;
  }

  setRecord(update: Update<ModuleWorkspaceRecord | null>): void {
    this.set({ record: typeof update === "function" ? update(this.state.record) : update });
  }

  setPloToMimlos(update: Update<Record<string, string[]>>): void {
    this.set({
      ploToMimlos: typeof update === "function" ? update(this.state.ploToMimlos) : update,
    });
  }

  setStatus(status: ModuleSaveStatus): void {
    this.set({ status });
  }

  setConflict(conflict: ModuleSaveConflict | null): void {
    this.set({ conflict });
  }

  setError(error: Error | null): void {
    this.set({ error });
  }

  private async send(adapter: ModuleSaveAdapter, input: SaveModuleWorkspaceInput) {
    const discards = this.discards;
    let saved: ModuleWorkspaceRecord;
    try {
      saved = await adapter.client.saveModuleWorkspace(input);
    } catch (cause) {
      // The base stays what was read. A retry against it is composed property
      // by property on the server, so it lands once the collisions are
      // resolved; a retry against the stored copy would match its hash and
      // write the draft over it wholesale.
      const conflict = readModuleConflict(cause);
      this.set({
        ...(conflict ? { conflict } : {}),
        status: "error",
        error: asError(cause, "Unable to save the module"),
      });
      return false;
    }
    if (discards !== this.discards) {
      return true;
    }
    this.adopt(saved, input);
    adapter.onAdopted?.();
    this.set({ conflict: null, status: "saved" });
    // The save landed whatever the refresh after it does.
    await Promise.resolve(adapter.onSaved?.()).catch(() => undefined);
    return true;
  }

  private set(next: Partial<ModuleTargetState>): void {
    this.state = { ...this.state, ...next };
    this.listeners.forEach((listener) => listener());
    this.resume();
  }

  /**
   * Nothing holds the save: not one already out, and not a failure waiting on
   * the reader, whose answer a retry would bury.
   */
  private sendable(): boolean {
    const { status, conflict } = this.state;
    return (
      this.pending &&
      status !== "saving" &&
      status !== "error" &&
      !conflict &&
      Boolean(this.adapter?.canEdit())
    );
  }

  private arm(): void {
    this.disarm();
    this.timer = setTimeout(() => {
      this.timer = null;
      if (this.sendable()) {
        this.flush().catch(() => undefined);
      }
    }, this.windowMs);
  }

  private disarm(): void {
    if (this.timer) {
      clearTimeout(this.timer);
      this.timer = null;
    }
  }

  private draftKey(): string | null {
    const { module, ploToMimlos } = this.state;
    return module ? serialized(module, ploToMimlos) : null;
  }
}
